/**
 * Validation utilities for forms
 */

export interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
}

/**
 * Email validation
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Phone number validation (Taiwan format)
 */
export const isValidPhone = (phone: string): boolean => {
  // Support formats: 0912345678, 0912-345-678, 02-23456789
  const phoneRegex = /^(\d{2,3}-?\d{7,8}|09\d{2}-?\d{3}-?\d{3})$/;
  return phoneRegex.test(phone.replace(/\s/g, ''));
};

/**
 * Password validation
 */
export const isValidPassword = (password: string): boolean => {
  // At least 6 characters
  return password.length >= 6;
};

/**
 * Required field validation
 */
export const validateRequired = (value: string, fieldName: string): string | null => {
  if (!value || value.trim().length === 0) {
    return `${fieldName} is required`;
  }
  return null;
};

/**
 * Email field validation
 */
export const validateEmail = (email: string): string | null => {
  const requiredError = validateRequired(email, 'Email');
  if (requiredError) {
    return requiredError;
  }

  if (!isValidEmail(email)) {
    return 'Please enter a valid email address';
  }

  return null;
};

/**
 * Phone field validation
 */
export const validatePhone = (phone: string): string | null => {
  if (!phone || phone.trim().length === 0) {
    return null; // Phone is optional
  }

  if (!isValidPhone(phone)) {
    return 'Please enter a valid phone number (e.g., 0912-345-678)';
  }

  return null;
};

/**
 * Password field validation
 */
export const validatePassword = (password: string): string | null => {
  const requiredError = validateRequired(password, 'Password');
  if (requiredError) {
    return requiredError;
  }

  if (!isValidPassword(password)) {
    return 'Password must be at least 6 characters';
  }

  return null;
};

/**
 * Confirm password validation
 */
export const validateConfirmPassword = (
  password: string,
  confirmPassword: string
): string | null => {
  const requiredError = validateRequired(confirmPassword, 'Confirm Password');
  if (requiredError) {
    return requiredError;
  }

  if (password !== confirmPassword) {
    return 'Passwords do not match';
  }

  return null;
};

/**
 * Login form validation
 */
export const validateLoginForm = (email: string, password: string): ValidationResult => {
  const errors: Record<string, string> = {};

  const emailError = validateEmail(email);
  if (emailError) {
    errors.email = emailError;
  }

  const passwordError = validatePassword(password);
  if (passwordError) {
    errors.password = passwordError;
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

/**
 * Register form validation
 */
export const validateRegisterForm = (data: {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  phone?: string;
}): ValidationResult => {
  const errors: Record<string, string> = {};

  // Display name
  const nameError = validateRequired(data.name, 'Name');
  if (nameError) {
    errors.name = nameError;
  }

  // Email
  const emailError = validateEmail(data.email);
  if (emailError) {
    errors.email = emailError;
  }

  // Password
  const passwordError = validatePassword(data.password);
  if (passwordError) {
    errors.password = passwordError;
  }

  // Confirm password
  const confirmPasswordError = validateConfirmPassword(
    data.password,
    data.confirmPassword
  );
  if (confirmPasswordError) {
    errors.confirmPassword = confirmPasswordError;
  }

  // Phone (optional)
  if (data.phone) {
    const phoneError = validatePhone(data.phone);
    if (phoneError) {
      errors.phone = phoneError;
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

/**
 * Wish form validation
 */
export const validateWishForm = (data: {
  title: string;
  category: string;
  region: string;
  userName: string;
  userPhone?: string;
  userEmail?: string;
}): ValidationResult => {
  const errors: Record<string, string> = {};

  // Title
  const titleError = validateRequired(data.title, 'Title');
  if (titleError) {
    errors.title = titleError;
  }

  // Category
  const categoryError = validateRequired(data.category, 'Category');
  if (categoryError) {
    errors.category = categoryError;
  }

  // Region
  const regionError = validateRequired(data.region, 'Region');
  if (regionError) {
    errors.region = regionError;
  }

  // User name
  const nameError = validateRequired(data.userName, 'Name');
  if (nameError) {
    errors.userName = nameError;
  }

  // User phone (optional)
  if (data.userPhone) {
    const phoneError = validatePhone(data.userPhone);
    if (phoneError) {
      errors.userPhone = phoneError;
    }
  }

  // User email (optional)
  if (data.userEmail) {
    const emailError = validateEmail(data.userEmail);
    if (emailError) {
      errors.userEmail = emailError;
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};
